import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Chip, Link, Stack, SxProps, Typography } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { Button } from 'system/atoms';
import { TableV3, TableV3ColDefs, TableV3Ref } from 'common/table';
import { NextButton } from 'components/NextButton';
import { useToast, ToastType } from 'services/toast';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import {
  useKomboJobsFetcher,
  KomboJobFilters
} from 'modules/ats/hooks/useKomboJobsFetcher';
import { KomboJob } from 'services/integration/types/komboJob';
import { AtsJobInput } from 'pages/CandidatesRanking/types';
import { mapKomboJobToAtsJobInput } from 'services/integration/utils/mapKomboJobToAtsJobInput';
import { sortKomboJobsConnectedToBottom } from 'services/integration/utils/sortKomboJobsConnectedToBottom';
import { TableV3Filters } from 'common/table/components/TableV3';
import { formatToDateTime } from 'utils/datetime';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { JobDescriptionFormData } from 'modules/ats/components/JobDescriptionHtmlDrawer';
import { parseAtsJobDescription } from '../utils';
import { useFunctionAfterUpdate } from 'common/hooks/useFunctionAfterUpdate';
import { LinkButton } from './LinkButton';
import { isJobConnectedToOwner } from 'services/integration/utils/isJobConnectedToOwner';

const headerContainer: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 4,
  marginBottom: 2
};

const emptyStateContainer: SxProps = {
  p: 4,
  textAlign: 'center',
  color: 'customGrey.dark'
};

type StatusValue = 'open' | 'published' | 'draft' | 'closed' | 'unknown';

const statusChipStyle = {
  open: { color: 'success.contrastText', backgroundColor: 'success.main' },
  published: { color: 'success.contrastText', backgroundColor: 'success.main' },
  draft: { color: 'customGrey.dark', backgroundColor: 'customGrey.medium' },
  closed: { color: 'customGrey.dark', backgroundColor: 'customGrey.light' },
  unknown: { color: 'customGrey.dark', backgroundColor: 'customGrey.light' }
} as const satisfies Record<StatusValue, SxProps>;

// Kombo job statuses accepted by `GET /v1/ats/jobs?statuses=...`. Values are
// passed back to the backend verbatim (Kombo expects upper-case).
const STATUS_FILTER_OPTIONS = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'Archived', value: 'ARCHIVED' }
];

type Props = {
  setAtsJobs: (atsJobs: AtsJobInput[]) => void;
  selectableRowsSingle?: boolean;
  handleNext?: () => void;
  disableNext?: boolean;
  nextText?: string;
  disableAddJobDescription: boolean;
  onAddJobDescription: () => void;
  isNextError?: boolean;
  isNextLoading?: boolean;
  isForAnalysis?: boolean;
  // Defaults to true; pass false to allow re-selecting connected jobs.
  disableJobsAlreadyConnected?: boolean;
};

const jobTitleCellContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%'
};

const connectedJobTitle: SxProps = {
  color: 'customGrey.dark',
  flex: 1
};

const normalizeStatus = (raw: string | null | undefined): StatusValue => {
  const v = raw?.toLowerCase() ?? '';
  if (v === 'open' || v === 'published') return v;
  if (v === 'draft') return 'draft';
  if (v === 'closed' || v === 'archived') return 'closed';
  return 'unknown';
};

const StatusChip: React.FC<{ raw: string | null | undefined }> = ({ raw }) => {
  const status = normalizeStatus(raw);
  return (
    <Chip label={raw ? raw.toLowerCase() : '-'} sx={statusChipStyle[status]} />
  );
};

const formatLocation = (location: KomboJob['location']) =>
  [location?.city, location?.state, location?.country]
    .filter(Boolean)
    .join(', ');

export const KomboJobTable: React.FC<Props> = ({
  setAtsJobs,
  selectableRowsSingle = false,
  handleNext,
  disableNext,
  nextText,
  disableAddJobDescription,
  onAddJobDescription,
  isNextError,
  isNextLoading,
  isForAnalysis = false,
  disableJobsAlreadyConnected = true
}) => {
  const tableRef = useRef<TableV3Ref<KomboJob>>(null);
  const organization = useOrganization();
  const { showToast } = useToast();
  const { openDrawer } = useDrawer();

  const isJobConnected = useCallback(
    (job: KomboJob) => {
      if (!disableJobsAlreadyConnected) return false;
      return isJobConnectedToOwner(job);
    },
    [disableJobsAlreadyConnected]
  );

  // Defer until setAtsJobs has flushed so the next step reads fresh state.
  const nextAfterUpdate = useFunctionAfterUpdate(() => handleNext?.());

  const handleChooseJobDescription = useCallback(
    (job: KomboJob, formData: JobDescriptionFormData) => {
      const base = mapKomboJobToAtsJobInput(job);
      const editedDescription = parseAtsJobDescription({
        jobTitle: formData.title,
        content: formData.description,
        locations: [{ id: '1', name: formData.location }]
      });
      setAtsJobs([{ ...base, jobDescription: editedDescription }]);
      nextAfterUpdate();
    },
    [setAtsJobs, nextAfterUpdate]
  );

  const openJobDescriptionDrawer = useCallback(
    (job: KomboJob) => {
      const formattedLocation = formatLocation(job.location);
      openDrawer({
        drawerContentType: DrawerComponentType.JobDescriptionHtml,
        drawerComponentProps: {
          jobDescription: job.description ?? '',
          title: job.name,
          locations: formattedLocation
            ? [{ id: '1', name: formattedLocation }]
            : [],
          onChoose: (data: JobDescriptionFormData) =>
            handleChooseJobDescription(job, data),
          hideCloseIconButton: true
        }
      });
    },
    [openDrawer, handleChooseJobDescription]
  );

  const [filters, setFilters] = useState<KomboJobFilters>({});
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const {
    pages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    pageSize
  } = useKomboJobsFetcher({
    organizationId: organization.id,
    filters,
    isForAnalysis
  });

  const currentPageRows = useMemo(
    () =>
      sortKomboJobsConnectedToBottom(
        pages[currentPageIndex] ?? [],
        isJobConnected
      ),
    [pages, currentPageIndex, isJobConnected]
  );

  // Inflate total so the "next" arrow stays enabled while more pages exist.
  const loadedRows = pages.reduce((acc, p) => acc + p.length, 0);
  const totalRows = hasNextPage ? loadedRows + pageSize : loadedRows;

  const onChangePage = useCallback(
    async (newPage: number) => {
      if (newPage > currentPageIndex && newPage >= pages.length) {
        if (hasNextPage && !isFetchingNextPage) {
          await fetchNextPage();
        }
      }
      setCurrentPageIndex(newPage);
    },
    [
      currentPageIndex,
      pages.length,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage
    ]
  );

  const onFiltersChanged = useCallback(
    (newFilters: TableV3Filters<KomboJob>) => {
      const search = (newFilters.searchFields ?? {}) as Partial<
        Record<keyof KomboJob, string>
      >;
      const filterValues = (newFilters.filterValues ?? {}) as Partial<
        Record<keyof KomboJob, { value: string }[]>
      >;
      const selectedStatuses = filterValues.status
        ?.map((item) => item.value)
        .join(',');

      setFilters({
        nameContains: search.name || undefined,
        remoteIds: search.remote_id || undefined,
        jobCodes: search.job_code || undefined,
        statuses: selectedStatuses || undefined
      });
      setCurrentPageIndex(0);
    },
    []
  );

  const onSelectionChanged = useCallback(
    (_selectedIds: string[], selectedRows: KomboJob[]) => {
      setAtsJobs(selectedRows.map(mapKomboJobToAtsJobInput));
    },
    [setAtsJobs]
  );

  const isRowSelectable = useCallback(
    (row: KomboJob) => !isJobConnected(row),
    [isJobConnected]
  );

  const columns: TableV3ColDefs<KomboJob, keyof KomboJob> = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Job title',
        searchable: true,
        searchPlaceholder: 'Search by title',
        flex: 2,
        minWidth: 280,
        cellRenderer: (params: ICellRendererParams<KomboJob>) => {
          const row = params.data;
          if (!row) return null;
          const connected = isJobConnected(row);
          return (
            <Box sx={jobTitleCellContainer}>
              {connected ? (
                <Typography variant="body2" sx={connectedJobTitle}>
                  {row.name}
                </Typography>
              ) : (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={(event) => {
                    // Don't toggle row selection on title click.
                    event.stopPropagation();
                    openJobDescriptionDrawer(row);
                  }}
                >
                  {row.name}
                </Link>
              )}
              {connected && (
                <LinkButton
                  campaignId={row.campaignId ?? undefined}
                  candidateBatchId={row.candidateBatchId ?? undefined}
                  isDraftCandidateBatch={row.isDraftCandidateBatch ?? undefined}
                  workflowId={row.workflowId ?? undefined}
                />
              )}
            </Box>
          );
        }
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1.5,
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<KomboJob>) =>
          formatLocation(params.data?.location) || '-'
      },
      {
        field: 'status',
        headerName: 'Job status',
        width: 160,
        cellRenderer: (params: ICellRendererParams<KomboJob>) => (
          <StatusChip raw={params.data?.status} />
        ),
        filterOptions: {
          items: STATUS_FILTER_OPTIONS,
          selectionType: 'multiple'
        }
      },
      {
        field: 'remote_id',
        headerName: 'Job ID',
        searchable: true,
        searchPlaceholder: 'Search by ID',
        width: 160,
        cellRenderer: (params: ICellRendererParams<KomboJob>) => {
          const row = params.data;
          if (!row) return null;
          return row.post_url ? (
            <Link href={row.post_url} target="_blank" rel="noreferrer">
              {row.remote_id}
            </Link>
          ) : (
            row.remote_id
          );
        }
      },
      {
        field: 'job_code',
        headerName: 'Job ref',
        searchable: true,
        searchPlaceholder: 'Search by ref',
        width: 140,
        cellRenderer: (params: ICellRendererParams<KomboJob>) =>
          params.data?.job_code || '-'
      },
      {
        field: 'changed_at',
        headerName: 'Last activity',
        width: 180,
        cellRenderer: (params: ICellRendererParams<KomboJob>) =>
          params.data?.changed_at
            ? formatToDateTime(params.data.changed_at)
            : '-'
      }
    ],
    [openJobDescriptionDrawer, isJobConnected]
  );

  return (
    <Box>
      <Box sx={headerContainer}>
        <Stack
          direction="row"
          gap={4}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Button
            variant="tertiary"
            startIcon={<RefreshIcon />}
            disabled={isLoading}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          {!disableAddJobDescription && (
            <Button
              variant="tertiary"
              startIcon={<AddIcon />}
              onClick={onAddJobDescription}
            >
              Add job description
            </Button>
          )}
        </Stack>

        {handleNext && (
          <NextButton
            nextText={nextText}
            handleNext={() => {
              if (disableNext) {
                showToast({
                  message: 'Choose a job.',
                  type: ToastType.Error
                });
                return;
              }
              handleNext();
            }}
            isError={isNextError}
            errorTitle="No jobs selected"
            errorMessage="Choose at least one job to continue."
            isLoading={isNextLoading}
          />
        )}
      </Box>
      <TableV3
        ref={tableRef}
        tableId="kombo-job-table"
        columns={columns}
        data={currentPageRows}
        defaultSort="unset"
        loading={isLoading || isFetchingNextPage}
        onFiltersChanged={onFiltersChanged}
        selectionOptions={{
          mode: selectableRowsSingle ? 'singleRow' : 'multiRow',
          identifier: 'id',
          onSelectionChanged,
          isRowSelectable
        }}
        paginationOptions={{
          pageSize,
          totalRows,
          currentPage: currentPageIndex,
          onChangePage
        }}
        emptyStateComponent={<Box sx={emptyStateContainer}>No jobs found</Box>}
      />
    </Box>
  );
};
